import type { IAttachment, IDependency } from '@pulse/shared';

import { apiFetch } from '../../../lib/api-client';
import { extractErrorMessage } from '../../../lib/api-error.util';

const base = (projectId: string) => `/api/v1/projects/${projectId}/dependencies`;

export interface CreateDependencyInput {
  title: string;
  description?: string | null;
  impact?: string | null;
  type?: string | null;
  category?: string | null;
  ownerId: string;
  /** `null` explicitly clears the due date on an existing dependency; `undefined` leaves it unset at creation. */
  dueDate?: string | null;
  priority?: string | null;
  linkedIssueIds?: string[];
  linkedRiskIds?: string[];
}
export type UpdateDependencyInput = Partial<CreateDependencyInput>;

export async function fetchDependencies(projectId: string): Promise<IDependency[]> {
  const res = await apiFetch(base(projectId));
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch dependencies'));
  return res.json() as Promise<IDependency[]>;
}

export async function createDependency(projectId: string, data: CreateDependencyInput): Promise<IDependency> {
  const res = await apiFetch(base(projectId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to create dependency'));
  return res.json() as Promise<IDependency>;
}

export async function updateDependency(projectId: string, dependencyId: string, data: UpdateDependencyInput): Promise<IDependency> {
  const res = await apiFetch(`${base(projectId)}/${dependencyId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update dependency'));
  return res.json() as Promise<IDependency>;
}

export async function transitionDependency(projectId: string, dependencyId: string, toStep: string): Promise<IDependency> {
  const res = await apiFetch(`${base(projectId)}/${dependencyId}/transition`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ toStep }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to change dependency status'));
  return res.json() as Promise<IDependency>;
}

export async function setDependencyActive(projectId: string, dependencyId: string, active: boolean): Promise<IDependency> {
  const res = await apiFetch(`${base(projectId)}/${dependencyId}/${active ? 'activate' : 'deactivate'}`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update dependency status'));
  return res.json() as Promise<IDependency>;
}

export async function fetchDependencyAttachments(projectId: string, dependencyId: string): Promise<IAttachment[]> {
  const res = await apiFetch(`${base(projectId)}/${dependencyId}/attachments`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch attachments'));
  return res.json() as Promise<IAttachment[]>;
}

export async function uploadDependencyAttachment(projectId: string, dependencyId: string, file: File): Promise<IAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiFetch(`${base(projectId)}/${dependencyId}/attachments`, { method: 'POST', body: formData });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to upload attachment'));
  return res.json() as Promise<IAttachment>;
}

export async function removeDependencyAttachment(projectId: string, dependencyId: string, attachmentId: string): Promise<void> {
  const res = await apiFetch(`${base(projectId)}/${dependencyId}/attachments/${attachmentId}/deactivate`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to remove attachment'));
}

export function dependencyAttachmentDownloadUrl(projectId: string, dependencyId: string, attachmentId: string): string {
  return `${base(projectId)}/${dependencyId}/attachments/${attachmentId}/download`;
}
